import React, { useState, useEffect } from 'react';
import { Video, BarChart3, Chrome, Zap, Smartphone, Lock, Info, ShoppingBag, Star, Users, TrendingUp, Search, Network, Calendar, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MyVideosDashboard from '../pages/MyVideosDashboard';
import CreateMediaKitBanner from '../components/mediaKit/CreateMediaKitBanner';
import { mediaKitApi } from '../services/mediaKitApi';
import MspGptBanner from '../components/MspGptBanner';

/* ── VideoTooltip (same as DashboardPage) ── */
const VideoTooltip = ({ videoUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
    if (!isOpen) setTimeout(() => setShowVideo(true), 300);
    else setShowVideo(false);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    setShowVideo(false);
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="ml-1 w-20 h-5 mt-1 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors cursor-pointer shadow-md hover:shadow-lg"
        title="Watch video"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className='-ml-2'>
          <circle cx="12" cy="12" r="10" fill="#ff000058" />
          <polygon points="9,7 9,17 17,12" fill="white" />
        </svg>
        <span className='text-[12px]'>Tutorial</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 animate-fadeIn" onClick={handleClose} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto bg-gray-900 rounded-xl shadow-2xl overflow-hidden border-2 border-red-600 animate-fadeIn max-w-2xl w-full">
              <div className="relative">
                {showVideo ? (
                  <iframe
                    width="100%" height="400"
                    src={`${videoUrl}?controls=1&modestbranding=1`}
                    title="Feature explanation"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen className="block"
                  />
                ) : (
                  <div className="w-full h-96 bg-gray-800 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                  </div>
                )}
                <button
                  onClick={handleClose}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

/* ── DescriptionTooltip (same as DashboardPage) ── */
const DescriptionTooltip = ({ description }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-3 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition flex items-center gap-2 text-sm font-semibold"
      >
        <Info size={16} />
        What's this?
      </button>
      {isOpen && (
        <div className="absolute left-0 mt-2 w-80 bg-gray-900 text-white p-4 rounded-lg shadow-2xl z-20 animate-fadeIn">
          <p className="text-sm leading-relaxed">{description}</p>
          <div className="absolute -top-2 left-6 w-4 h-4 bg-gray-900 transform rotate-45"></div>
        </div>
      )}
    </div>
  );
};

/* ── Extension tutorial (banner + small button share one video modal) ── */
const EXT_TUTORIAL_ID = 'aalZVCg91Kw';

const TutorialModal = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 animate-fadeIn" onClick={onClose}>
      <div className="relative w-full max-w-4xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl border-2 border-red-600" onClick={(e) => e.stopPropagation()}>
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${EXT_TUTORIAL_ID}?autoplay=1&rel=0&modestbranding=1`}
            title="MIP Chrome Extension tutorial"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
        <button onClick={onClose} className="absolute top-2 right-2 w-9 h-9 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-lg text-lg">✕</button>
      </div>
    </div>
  );
};

const WatchTutorialButton = ({ label = 'Watch tutorial' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-semibold shadow-md transition"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><polygon points="8,5 8,19 19,12" /></svg>
        {label}
      </button>
      {open && <TutorialModal onClose={() => setOpen(false)} />}
    </>
  );
};

const ExtensionTutorialBanner = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-12 bg-white rounded-2xl shadow-xl border-2 border-red-200 overflow-hidden">
      <div className="flex flex-col md:flex-row">
        <button
          onClick={() => setOpen(true)}
          className="relative md:w-2/5 aspect-video md:aspect-auto bg-gray-900 group flex-shrink-0"
          aria-label="Play the extension tutorial"
        >
          <img
            src={`https://img.youtube.com/vi/${EXT_TUTORIAL_ID}/hqdefault.jpg`}
            alt="Extension tutorial"
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-red-600 group-hover:bg-red-700 group-hover:scale-110 transition flex items-center justify-center shadow-2xl">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="white" className="ml-1"><polygon points="7,4 7,20 20,12" /></svg>
            </div>
          </div>
          <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow">START HERE</span>
        </button>

        <div className="p-6 md:p-8 flex-1">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            Most of our AIP tools live in our Chrome Extension & Mobile App
          </h2>
          <p className="text-gray-600 mb-4">
            Watch this quick tutorial to learn how to find the best campaigns, message brands in bulk, and get paid for the videos you already made.
          </p>
          <ul className="grid sm:grid-cols-2 gap-2 mb-6 text-sm text-gray-700">
            {[
              '🚀 MIP Power Search 1.0 & 2.0',
              '🎥 Find Campaigns for My Videos',
              '✉️ MIP Messages & Templates',
              '🤖 Outreach Clone (auto-messaging)',
              '✅ Bulk Accept & Video Links',
              '📊 Carousel & competition data',
            ].map(item => <li key={item} className="flex items-center gap-2">{item}</li>)}
          </ul>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold shadow-lg transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><polygon points="7,4 7,20 20,12" /></svg>
              Watch the Tutorial
            </button>
            <a
              href="https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 shadow-lg transition"
            >
              <Chrome size={18} />
              Get the Chrome Extension
            </a>
            <a
              href="/mobile-app"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-purple-300 text-purple-700 rounded-lg font-semibold hover:bg-purple-50 shadow-lg transition"
            >
              <Smartphone size={18} />
              Our Mobile App
            </a>
          </div>
        </div>
      </div>
      {open && <TutorialModal onClose={() => setOpen(false)} />}
    </div>
  );
};

/* ── InfluencerToolCard ── */
const InfluencerToolCard = ({
  icon: Icon,
  title,
  description,
  tags = [],
  onClick,
  locked = false,
  requiredPlan = '',
  onUpgrade,
  videoUrl,
  comingSoon = false,
}) => {
  return (
    <div
      onClick={locked || comingSoon ? undefined : onClick}
      className={`bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition border-2 border-transparent ${!locked && !comingSoon ? 'hover:border-blue-400' : ''} ${!locked && !comingSoon && onClick ? 'cursor-pointer' : 'cursor-default'} relative`}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-1">
              {title}
              {!comingSoon && videoUrl && <VideoTooltip videoUrl={videoUrl} />}
            </h3>
            {comingSoon && (
              <span className="ml-auto text-xs bg-purple-100 text-purple-700 border border-purple-300 font-semibold px-3 py-1 rounded-full">
                Coming Soon
              </span>
            )}
          </div>

          {comingSoon ? (
            <p className="text-gray-400 leading-relaxed italic text-sm">{description}</p>
          ) : locked ? (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-gray-700 font-semibold text-sm">
                <Lock className="text-gray-400" size={20} />
                MIP {requiredPlan} Plan Required
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={onUpgrade}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition shadow-lg"
                >
                  Upgrade to Unlock
                </button>
                <DescriptionTooltip description={description} />
              </div>
            </div>
          ) : (
            <>
              <p className="text-gray-600 leading-relaxed mb-3">{description}</p>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-auto">
                  <span className="text-xs text-gray-500">{tags.join(' • ')}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Main InfluencerDashboardPage ── */
const InfluencerDashboardPage = ({ user }) => {
  const navigate = useNavigate();
  const [myKits, setMyKits] = useState([]);

  useEffect(() => {
    mediaKitApi.listMine()
      .then(({ kits }) => setMyKits(kits || []))
      .catch(() => { });
  }, []);

  const currentPlan = user?.subscription_plan || 'No plan';
  const isSubscriptionActive = user?.subscription_active || false;

  const isPlanUnlocked = (requiredPlan) => {
    if (!isSubscriptionActive) return false;
    if (currentPlan === 'No plan') return false;
    if (currentPlan.startsWith('MSP_')) return false; // MSP-only users get nothing here
    const planHierarchy = {
      'Beginner': 1, 'Pro': 2, 'Unlimited': 3,          // bundle
      'MIP_Beginner': 1, 'MIP_Pro': 2, 'MIP_Unlimited': 3,  // mip only
    };
    return planHierarchy[currentPlan] >= planHierarchy[requiredPlan];
  };

  const handleUpgrade = () => navigate('/pricing');

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return 'Ready to record some reviews?';
    if (hour >= 12 && hour < 17) return "Let's grow!";
    if (hour >= 17 && hour < 21) return 'One more video before dinner?';
    return 'Late night recordings?';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .blur-section { filter: blur(4px); pointer-events: none; user-select: none; }

        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
          50% { box-shadow: 0 0 20px 8px rgba(59, 130, 246, 0.5); }
        }

        @media (max-width: 640px) {
          .mobile-app-btn {
            background: linear-gradient(to right, #3b82f6, #7c3aed);
            color: white;
            border-color: #2563eb;
            animation: pulse-glow 1.8s ease-in-out infinite;
          }
        }
      `}</style>

      {/* Animated background blobs */}
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(37,99,235) 0%, rgb(124,58,237) 100%)' }} />
        <div className="absolute top-0 -right-4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(37,99,235) 0%, rgb(124,58,237) 100%)' }} />
        <div className="absolute -bottom-8 left-20 w-96 h-96 rounded-full filter blur-3xl" style={{ background: 'linear-gradient(90deg, rgb(37,99,235) 0%, rgb(124,58,237) 100%)' }} />
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-9">
          <div className="flex gap-6 flex-wrap items-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">{getGreeting()}</h1>
            <div className="mt-1">
              <span className={`inline-block ${currentPlan === 'No plan' || !isSubscriptionActive ? 'bg-gray-400' : 'bg-gradient-to-r from-blue-600 to-purple-600'} text-white px-4 py-2 rounded-full font-semibold text-sm`}>
                Current Plan: {currentPlan.replace("_", " ")} {!isSubscriptionActive && currentPlan !== 'No plan' && '(Inactive)'}
              </span>
              {(currentPlan === 'No plan' || !isSubscriptionActive) && (
                <button
                  onClick={handleUpgrade}
                  className="ml-3 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-semibold text-sm hover:opacity-90 transition shadow-lg"
                >
                  {currentPlan === 'No plan' ? 'Get Started' : 'Reactivate'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* App links banner */}
        <div className="mb-6 bg-white border border-blue-200 rounded-lg px-3 py-3 sm:px-4 sm:py-2 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
          <p className="text-blue-800 font-medium text-sm text-center sm:text-left">Looking for our AIP apps?</p>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <a
              href="/mobile-app"
              className="mobile-app-btn flex items-center justify-center gap-1.5 bg-blue-100 text-blue-900 px-3 py-2 sm:py-1 rounded-lg hover:bg-blue-200 transition text-sm font-medium border border-blue-200 w-full sm:w-auto"
            >
              <Smartphone size={14} />
              AIP Mobile App
            </a>

            <a
              href="https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob"
              className="flex items-center justify-center gap-1.5 bg-blue-100 text-blue-900 px-3 py-2 sm:py-1 rounded-lg hover:bg-blue-200 transition text-sm font-medium border border-blue-200 w-full sm:w-auto"
              target="_blank" rel="noreferrer"
            >
              <Chrome size={14} />
              AIP Chrome Extension
            </a>
          </div>

          <div className="w-full h-px sm:w-px sm:h-5 bg-blue-300 opacity-60 sm:block" />

          <a
            href="/webinars"
            className="flex items-center justify-center gap-2 bg-blue-700 text-blue-50 px-3 py-2 sm:py-1 rounded-lg hover:bg-blue-800 transition text-sm font-medium w-full sm:w-auto"
            target="_blank" rel="noreferrer"
          >
            <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse flex-shrink-0" />
            <Calendar size={14} />
            Join Weekly Webinar
          </a>
        </div>

        <div className="mb-8">
          <MyVideosDashboard user={user} compact={true} onViewAll={() => navigate('/aip/my-videos')} />
        </div>

        <MspGptBanner tool="influencer" locked={!isPlanUnlocked('Pro')} onUpgrade={handleUpgrade} />

        <CreateMediaKitBanner kitCount={myKits.length} />

        <ExtensionTutorialBanner />

        {/* ── Creator Tools ── */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Video className="text-blue-600" size={32} />
            Video Tools
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <InfluencerToolCard
              icon={Smartphone}
              title="AIP Mobile App"
              description="Scan any product by barcode or image. See AIP data instantly and record your review with AI-generated scripts - faster and better."
              tags={['Barcode Scan', 'Video Studio', 'AI Image Search']}
              onClick={() => window.location.href = '/mobile-app'}
              locked={!isPlanUnlocked('Beginner')}
              requiredPlan="Beginner"
              onUpgrade={handleUpgrade}
              videoUrl="https://www.youtube.com/embed/S2zD3I7ajdQ"
            />
            <InfluencerToolCard
              icon={Chrome}
              title="AIP Chrome Extension"
              description="Get instant AIP insights on any Amazon product page. See key data and use our auto-upload feature to auto-tag ASINs, set titles, add thumbnails, voiceovers, and more."
              tags={['Video Studio', 'Upper Carousel', 'Bottom Carousel']}
              onClick={() => window.location.href = 'https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob'}
              locked={!isPlanUnlocked('Beginner')}
              requiredPlan="Beginner"
              onUpgrade={handleUpgrade}
              videoUrl="https://www.youtube.com/embed/aalZVCg91Kw"
            />
            <InfluencerToolCard
              icon={Chrome}
              title="AIP Thumbnail Maker"
              description="Get instant AIP professional thumbnail using our amazing Thumbnail Maker!"
              tags={['Video Studio', 'Thumbnail', 'Professional']}
              onClick={() => window.location.href = 'https://www.multisellerpro.com/free-aip-thumbnail-maker'}
              locked={!isPlanUnlocked('Beginner')}
              requiredPlan="Beginner"
              onUpgrade={handleUpgrade}
            />
          </div>
        </div>

        {/* ── Research Tools ── */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Search className="text-blue-600" size={32} />
            Research
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <InfluencerToolCard
              icon={FileSpreadsheet}
              title="AIP CSV Scan"
              description="Upload a list of ASINs and instantly see which have Creator Connections campaigns, plus commission, budget, video competition, BSR, sales and estimated income. All scans are saved so you can come back anytime."
              tags={['Bulk ASIN Scan', 'Campaign Finder', 'Saved History']}
              onClick={() => navigate('/aip/csv-scan')}
              locked={!isPlanUnlocked('Pro')}
              requiredPlan="Pro"
              onUpgrade={handleUpgrade}
            />
            <InfluencerToolCard
              icon={BarChart3}
              title="Storefront Scan"
              description="Analyze any AIP influencer storefront. See their videos, stats, and competition - and double down on what works."
              tags={['Storefront Stalk', 'Daily Emails', 'Research']}
              onClick={() => navigate('/aip/storefront-scan')}
              locked={!isPlanUnlocked('Pro')}
              requiredPlan="Pro"
              onUpgrade={handleUpgrade}
            />
          </div>
        </div>

        {/* ── Community Tools ── */}
        <div className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Network className="text-blue-600" size={32} />
              Creator Connections
              <span className="text-sm font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-full flex items-center gap-1">
                <Chrome size={14} /> In the Chrome Extension
              </span>
            </h2>
            <WatchTutorialButton label="Watch how it works" />
          </div>
          <p className="text-gray-600 mb-6">These tools run inside the MIP Chrome Extension on the Amazon Creator Connections page. Watch the tutorial to get set up in minutes.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <InfluencerToolCard
              icon={ShoppingBag}
              title="MIP Power Search"
              description="Find under-reviewed products in your niche with high commission potential. We show competition, commission rate, and review demand so you focus on the best bets."
              tags={['Niche Research', 'Low Competition', 'High Commission']}
              onClick={() => navigate('/aip/creator-connections')}
              videoUrl="https://www.youtube.com/embed/aalZVCg91Kw"
              locked={!isPlanUnlocked('Pro')}
              requiredPlan="Pro"
              onUpgrade={handleUpgrade}
            />
            <InfluencerToolCard
              icon={BarChart3}
              title="Find Campaigns for My Videos"
              description="Find which videos from your storefront have campaigns so you won't miss the extra commissions!"
              tags={['Creator Connections', 'Campaigns', 'Research']}
              onClick={() => navigate('/aip/creator-connections')}
              videoUrl="https://www.youtube.com/embed/aalZVCg91Kw"
              locked={!isPlanUnlocked('Pro')}
              requiredPlan="Pro"
              onUpgrade={handleUpgrade}
            />
            <InfluencerToolCard
              icon={BarChart3}
              title="MIP Messages"
              description="Use our message templates and quick responses to quickly communicate with brands!"
              tags={['Creator Connections', 'Templates', 'Chat']}
              onClick={() => navigate('/aip/creator-connections')}
              videoUrl="https://www.youtube.com/embed/aalZVCg91Kw"
              locked={!isPlanUnlocked('Pro')}
              requiredPlan="Pro"
              onUpgrade={handleUpgrade}
            />
          </div>
        </div>

        {/* ── Upcoming Tools ── */}
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Zap className="text-blue-600" size={32} />
            Upcoming Tools - Must Wait
          </h2>
          <div className="bg-white rounded-xl shadow-2xl p-8 border-2 border-purple-400 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg z-30">
              Coming Very Soon
            </div>

            <div className="blur-section mt-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Video className="text-white" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Mystery Creator Tool #1 - You Won't Believe It</h3>
                  <p className="text-gray-600 leading-relaxed">
                    This tool will 10x your AIP income. Scientists are still figuring out how it's legal.
                  </p>
                </div>
              </div>
              <div className="space-y-3 mt-6">
                {[
                  'Auto-review 1000 products while you sleep 💤',
                  'Amazon algorithm mind-control button 🧠',
                  'Instant viral video generator ✨',
                  'One-click "Make Jeff Bezos your friend" 📞',
                  '"Go viral" button (patent pending) 🚀',
                ].map((f, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-blue-600">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/80 rounded-xl">
              <div className="text-center p-6">
                <div className="mb-3"><span className="text-5xl">🎬</span></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Game-Changing Tools Loading...</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  <span className="font-bold text-purple-600">Lock in MIP Unlimited NOW!</span>
                </p>
                <p className="text-gray-600 text-xs mb-2">
                  Early subscribers get <strong>lifetime pricing</strong>.<br />
                  Price increases after these tools launch!
                </p>
                <p className="text-blue-600 text-xs font-semibold mb-4">
                  These tools will transform your AIP business
                </p>
                <button
                  onClick={handleUpgrade}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition shadow-lg"
                >
                  Lock In Price Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default InfluencerDashboardPage;
